<template>
  <div
    ref="rootElementRef"
    class="field-select-search"
    :class="{ 'field-select-search--small': size === 'small' }"
  >
    <div
      ref="dropdownAnchorEl"
      class="field-select-search__control"
      @click="fieldSelectSearchControlClickHandler"
    >
      <slot name="activation-dropdown">
        <field-select
          v-bind="$attrs"
          ref="fieldSelectRef"
          :open="open"
          :errors="errors"
          :model-value="modelValue"
          :clearable="false"
          :class="{ 'q-field--focused': showDropdown }"
          :placeholder="placeholder"
          :autofocus="autofocus"
          :size="size"
          :is-disabled="isDisabled"
          :is-focused="isFocused"
          :flat="flat"
          :transparent="transparent"
          hide-no-result
          :error-state="errorState"
          :bottom-slots="errorState && !!$slots.error"
          :multiple="multiple"
          :tab-index="tabIndex"
          :is-stateless="isStateless"
          @keydown="onKeydownSelect"
          @focus="focusHandler"
          @blur="blurHandler"
          @popup-hide="popupHideHandler"
          @popup-show="popupShowHandler"
        >
          <template v-slot:selected-item="{ opt }">
            <slot
              name="selected-item"
              :opt="opt"
              :option="optionsMap[opt]"
            >
              <span class="truncate">
                {{ optionsMap[opt]?.[optionLabel] }}
              </span>
            </slot>
          </template>

          <template v-slot:error>
            <slot name="error" />
          </template>

          <template
            v-if="$slots['label']"
            v-slot:label
          >
            <slot name="label" />
          </template>
        </field-select>
      </slot>
    </div>
    <div
      v-if="hint"
      class="mt-1 body-text-regular-12 lh-16 distinct--text"
    >
      {{ hint }}
    </div>
    <tm-dropdown
      ref="dropdownEl"
      v-model="showDropdown"
      :close-on-content-click="closeOnContentClick"
      :close-on-outside-click="closeOnOutsideClick"
      :target="dropdownAnchorEl"
      :position="dropdownPosition"
      hide-dropdown-wrapper
      no-parent-event
      :no-focus-on-show="hasSearch"
      scrollbar-style="thumb-with-track-hover"
      :max-height="menuMaxHeight"
      :overflow-hidden="overflowHidden"
      :width="menuWidth"
      :menu-fit="menuFit"
      @hide="$emit('hide')"
    >
      <template v-slot:menu>
        <component
          :is="listComponent"
          ref="listComponentElement"
          class="flex-grow-1"
          :class="[
            interactiveListClass,
            hasSearch || $slots['menu-header'] ? 'pt-4' : 'pt-2',
            { 'pb-1': !$slots['menu-footer'] },
          ]"
          :show-dropdown="showDropdown"
          :selected-index="selectedIndex"
          :options="filteredOptions"
          :option-label="optionLabel"
          :loading="loading"
          :show-skeleton="showSkeleton"
          :load-more-items-handler="loadMoreItems"
          :is-loading-more-page="isLoadingMorePage"
          :is-loading-first-page="isLoadingFirstPage"
          :is-disabled-options="isDisabledOptions"
          :is-loading-options="isLoadingOptions"
          :filter-value="filterValue"
          :filter-placeholder="filterPlaceholder"
          :debounce="debounce"
          :subheader-style="subheaderStyle"
          :item-style="itemStyle"
          :has-search="hasSearch"
          @select="onSelect"
          @update:filter="onFilter"
        >
          <template
            v-if="$slots['menu-header']"
            v-slot:menu-header
          >
            <slot name="menu-header" />
          </template>
          <template v-slot:override-option="{ opt, focused, focusFilter }">
            <slot
              name="override-option"
              v-bind="{ opt, focused, focusFilter }"
            />
          </template>
          <template v-slot:option="{ opt }">
            <slot
              name="option"
              v-bind="{ opt, filterValue, closeDropdown }"
            />
          </template>
          <template v-slot:menu-footer>
            <slot name="menu-footer" />
          </template>
          <template v-slot:skeleton-list>
            <slot name="skeleton-list" />
          </template>
        </component>
      </template>
    </tm-dropdown>
  </div>
</template>

<script setup lang="ts">
import type { PropType } from '@/composition/vue/compositionApi'
import { computed, onMounted, ref, useSlots, watch } from '@/composition/vue/compositionApi'
import TmDropdown from '@/components/shared/TmDropdown.vue'
import { keyCodes } from '@/constants/keyCodes'
import FieldSelect from '@/components/forms/fields/FieldSelect.vue'
import {
  fieldEmits,
  selectOptionLabelKey,
  selectOptionValueKey,
  type SelectSearchOption,
  type SelectValue,
} from '@/services/forms/types'
import { useField } from '@/composition/forms/useField'
import type { SizeProp } from '@/components/shared/types'
import { fieldProps } from '@/components/forms/fieldBase'
import type { Dict } from '@/types'
import SelectSearchInteractiveList from '@/components/forms/fields/selectSearch/SelectSearchInteractiveList.vue'
import SelectSearchVirtualList from '@/components/forms/fields/selectSearch/SelectSearchVirtualList.vue'
import { escapeText } from '@/utils/string/escapeText'
import type { ControlPosition } from '@/composition/tooltip/renderTooltip'
import type { IBaseListOption } from '@/components/forms/fields/types'
import type { ComponentExposed } from '@/composition/types'

const { keyEnter, keySpace, keyDown, keyUp } = keyCodes

type Value = SelectValue | SelectValue[]
type Option = SelectSearchOption
interface FakeOption extends IBaseListOption {
  id: string
}

type ListComponentElement =
  | ComponentExposed<typeof SelectSearchVirtualList>
  | ComponentExposed<typeof SelectSearchInteractiveList>

const getFakeItems = () => {
  const fakeItems: FakeOption[] = []
  for (let i = 0; i < 10; i++) {
    fakeItems.push({
      id: i.toString(),
    })
  }
  return fakeItems
}

defineOptions({
  inheritAttrs: false,
})

const props = defineProps({
  ...fieldProps,
  modelValue: {
    type: [String, Array] as PropType<Value>,
    required: true,
  },
  open: {
    type: Boolean,
  },
  options: {
    type: Array as PropType<Option[]>,
  },
  optionLabel: {
    type: String,
    default: selectOptionLabelKey,
  },
  optionValue: {
    type: String,
    default: selectOptionValueKey,
  },
  filterPlaceholder: {
    type: String,
    default: 'Search',
  },
  filterFunction: {
    type: Function as PropType<(option: Option, q: RegExp, rawQuery: string) => boolean>,
    default: (el: Option, q: RegExp) => el.subheader || q.test(el.text),
  },
  showSkeleton: {
    type: Boolean,
  },
  loading: {
    type: Boolean,
  },
  offFiltering: {
    type: Boolean,
  },
  autofocus: {
    type: Boolean,
  },
  size: {
    type: String as SizeProp<'' | 'small' | 'medium'>,
  },
  menuMaxHeight: {
    type: String,
  },
  subheaderStyle: {
    type: String as PropType<'bold' | 'caption'>,
    default: 'bold',
  },
  itemStyle: {
    type: String as PropType<'regular' | 'semi-bold'>,
    default: 'regular',
  },
  debounce: {
    type: Number,
  },
  isDisabledOptions: {
    type: Boolean,
    default: false,
  },
  isLoadingOptions: {
    type: Boolean,
    default: false,
  },
  closeOnContentClick: {
    type: Boolean,
    default: true,
  },
  closeOnOutsideClick: {
    type: Boolean,
    default: true,
  },
  errorState: {
    type: Boolean,
    default: false,
  },
  isLoadingFirstPage: {
    type: Boolean,
  },
  isLoadingMorePage: {
    type: Boolean,
  },
  notFilter: {
    type: Boolean,
  },
  interactiveListClass: {
    type: String,
  },
  dropdownPosition: {
    type: String as PropType<ControlPosition>,
  },
  multiple: {
    type: Boolean,
  },
  // menuFit and menuWidth: '1px' makes the behavior of the QMenu width equal to the anchor width.
  menuFit: {
    type: Boolean,
    default: true,
  },
  menuWidth: {
    type: String,
    default: '1px',
  },
  hasSearch: {
    type: Boolean,
    default: true,
  },
  overflowHidden: {
    type: Boolean,
    default: true,
  },
  useVirtualList: {
    type: Boolean,
    default: false,
  },
  widthForSmallSize: {
    type: String,
    default: '320px',
  },
})

const emit = defineEmits({
  ...fieldEmits<Value>(),
  show: () => true,
  hide: () => true,
  'update:filter': (_val: string) => true,
  'load-more-items': () => true,
  focus: (_event: FocusEvent) => true,
  blur: (_event: FocusEvent) => true,
})

defineSlots<{
  'activation-dropdown'?: () => any
  'selected-item'?: (props: { opt: any; option: any }) => any
  error?: () => any
  'override-option'?: (props: { opt: any; focused: boolean; focusFilter: () => void }) => any
  option?: (props: { opt: any; filterValue: string }) => any
  'menu-header'?: () => any
  'menu-footer'?: () => any
  'skeleton-list'?: () => any
  label?: () => any
}>()

const slots = useSlots()

const { emitInput } = useField<Value>(props, emit)

const rootElementRef = ref<HTMLDivElement>()
const fieldSelectRef = ref()
const listComponentElement = ref<ListComponentElement | undefined>()
const dropdownAnchorEl = ref()
const selectedIndex = ref(-1)
const showDropdown = ref(false)
const filterValue = ref('')

const listComponent = computed(() => (props.useVirtualList ? SelectSearchVirtualList : SelectSearchInteractiveList))

const mappedOptions = computed(() => {
  return props.options!.map((t) => {
    let disabled = t.disabled
    if ('disable' in t) {
      disabled = t.disable
    }
    return {
      ...t,
      disabled,
    } satisfies IBaseListOption
  })
})

const optionsMap = computed(() =>
  mappedOptions.value!.reduce(
    (acc, el: Option, index) => {
      acc[el[props.optionValue]] = {
        ...el,
        index,
      }
      return acc
    },
    {} as Dict<Option & { index: number }>,
  ),
)

const toggleDropdown = (val: boolean) => {
  if (props.isDisabled) {
    return
  }
  onFilter('')
  showDropdown.value = val
  if (val) {
    emit('show')
  } else {
    emit('hide')
  }

  const lastValue = Array.isArray(props.modelValue) ? props.modelValue.at(-1) : props.modelValue

  if (showDropdown.value && lastValue && optionsMap.value[lastValue]) {
    selectedIndex.value = optionsMap.value[lastValue].index
  }
}

const closeDropdown = () => toggleDropdown(false)

const filteredOptions = computed<IBaseListOption[]>(() => {
  if (props.isLoadingFirstPage) {
    return getFakeItems()
  }
  if (props.notFilter || !filterValue.value) return mappedOptions.value || []
  const query = new RegExp(escapeText(filterValue.value), 'i')

  return (mappedOptions.value || [])
    .filter((el: Option) => props.filterFunction(el, query, filterValue.value))
    .filter((el: Option, i: number, arr: Option[]) => {
      // remove empty subheadings
      const nextItem = arr[i + 1]
      const nextNotSubheader = !nextItem?.subheader

      return !el.subheader || (el.subheader && nextItem && nextNotSubheader)
    })
})

const onFilter = (val: string) => {
  filterValue.value = val
  emit('update:filter', val)
}

const onSelect = (opt: IBaseListOption) => {
  if (opt.disabled || props.isDisabledOptions || props.isLoadingOptions || props.isLoadingFirstPage) {
    return undefined
  }

  if (props.closeOnContentClick && !props.multiple) {
    toggleDropdown(false)
  }

  const selectedValue = opt[props.optionValue]

  if (!Array.isArray(props.modelValue)) {
    return emitInput(selectedValue)
  }

  if (props.modelValue.find((value) => value === selectedValue)) {
    return emitInput(props.modelValue.filter((value) => value !== selectedValue))
  }

  return emitInput([...props.modelValue, selectedValue])
}

const onKeydownSelect = (e: KeyboardEvent) => {
  if (listComponentElement.value && !props.useVirtualList && !props.hasSearch) {
    const typedElement = listComponentElement.value as ComponentExposed<typeof SelectSearchInteractiveList>
    typedElement.onKeydown(e)
  }
  if ([keyEnter, keySpace, keyUp, keyDown].includes(e.code)) {
    toggleDropdown(true)
  }
}

const loadMoreItems = () => {
  emit('load-more-items')
}

const focusHandler = (e: FocusEvent) => {
  emit('focus', e)
}

const blurHandler = (e: FocusEvent) => {
  if (!e) {
    return
  }

  const target = (e.relatedTarget ?? e.target) as HTMLElement

  if (rootElementRef.value?.contains(target)) {
    return
  }

  if (target?.closest('.tm-dropdown')) {
    return
  }

  emit('blur', e)
}

const hasActivationDropdownSlot = () => !!slots['activation-dropdown']

const popupHideHandler = async () => {
  if (hasActivationDropdownSlot()) {
    return
  }
  // QSelect's internal popup hides on outside-click / Escape independently of TmDropdown's persistent flag.
  // Without this guard the closeOnOutsideClick=false prop would be effectively ignored because QSelect
  // would force-close TmDropdown via this handler. Explicit selection still closes via onSelect.
  if (!props.closeOnOutsideClick) {
    return
  }
  toggleDropdown(false)
}

const popupShowHandler = () => {
  if (hasActivationDropdownSlot()) {
    return
  }
  toggleDropdown(true)
}

const fieldSelectSearchControlClickHandler = () => {
  if (!hasActivationDropdownSlot()) {
    return
  }
  toggleDropdown(!showDropdown.value)
}

const focus = () => {
  fieldSelectRef.value.focus()
}

const blur = () => {
  fieldSelectRef.value.hidePopup()
  fieldSelectRef.value.blur()
}

onMounted(() => {
  if (props.autofocus) {
    toggleDropdown(true)
  }
})

watch(
  () => props.open,
  (value) => {
    if (value) {
      toggleDropdown(true)
    }
  },
  { immediate: true },
)

// Sync QSelect popup state for closes that bypass onSelect (menu-footer auto-close).
watch(showDropdown, (value) => {
  if (!value) {
    fieldSelectRef.value?.hidePopup()
  }
})

defineExpose({
  focus,
  blur,
})
</script>

<style lang="scss" scoped>
.field-select-search {
  outline: none;
  &--small {
    width: v-bind(widthForSmallSize);
    max-width: 100%;
  }
}
</style>
